import { supabase } from './supabaseClient';

/**
 * Enhanced Scoring Engine Service
 *
 * Comprehensive AI-powered scoring system implementing the detailed algorithms
 * specified in the update.md document for immigration pathway matching.
 */
export class EnhancedScoringEngine {

  /**
   * Calculate comprehensive scores for all available pathways
   * @param {Object} userProfile - Complete user profile data
   * @param {Object} cvAnalysis - Optional CV analysis data
   * @param {string} countryCode - ISO country code
   * @returns {Object} - Comprehensive scoring results with AI insights
   */
  static async calculateAllPathwayScores(userProfile, cvAnalysis = null, countryCode = 'DEU') {
    try {
      const results = {};

      // Calculate scores for each supported pathway based on country
      if (countryCode === 'DEU' || countryCode === 'deu' || countryCode === 'germany') {
        results.germanyStudentVisa = await this.calculateGermanyStudentVisaScore(userProfile, cvAnalysis);
        results.chancenkarte = await this.calculateChancenkarteScore(userProfile, cvAnalysis);
        results.jobSeekerVisa = await this.calculateJobSeekerVisaScore(userProfile, cvAnalysis);
        results.workPermit = await this.calculateWorkPermitScore(userProfile, cvAnalysis);
      } else if (countryCode === 'CAN' || countryCode === 'can' || countryCode === 'canada') {
        results.canadaStudentVisa = await this.calculateCanadaStudentVisaScore(userProfile, cvAnalysis);
        results.canadaOneYearPG = await this.calculateCanadaOneYearPGScore(userProfile, cvAnalysis);
        results.canadaTwoYearPG = await this.calculateCanadaTwoYearPGScore(userProfile, cvAnalysis);
        results.expressEntry = await this.calculateExpressEntryScore(userProfile, cvAnalysis);
        results.provincialNomineeProgram = await this.calculateProvincialNomineeScore(userProfile, cvAnalysis);
      } else if (countryCode === 'AUS' || countryCode === 'aus' || countryCode === 'australia') {
        results.australiaStudentVisa = await this.calculateAustraliaStudentVisaScore(userProfile, cvAnalysis);
        results.australiaMasters = await this.calculateAustraliaMastersScore(userProfile, cvAnalysis);
        results.australiaGraduateVisa = await this.calculateAustraliaGraduateVisaScore(userProfile, cvAnalysis);
      } else if (countryCode === 'GBR' || countryCode === 'gbr' || countryCode === 'uk' || countryCode === 'united kingdom') {
        results.ukStudentVisa = await this.calculateUKStudentVisaScore(userProfile, cvAnalysis);
        results.ukGraduateRoute = await this.calculateUKGraduateRouteScore(userProfile, cvAnalysis);
        results.ukSkilledWorker = await this.calculateUKSkilledWorkerScore(userProfile, cvAnalysis);
      } else if (countryCode === 'NZL' || countryCode === 'nzl' || countryCode === 'new zealand') {
        results.newZealandStudentVisa = await this.calculateNewZealandStudentVisaScore(userProfile, cvAnalysis);
        results.newZealandWorkVisa = await this.calculateNewZealandWorkVisaScore(userProfile, cvAnalysis);
        results.newZealandSkilledMigrant = await this.calculateNewZealandSkilledMigrantScore(userProfile, cvAnalysis);
      }

      // Add comprehensive analysis
      const analysis = await this.generateComprehensiveAnalysis(userProfile, results, cvAnalysis);

      return {
        country: countryCode,
        userProfile: userProfile,
        timestamp: new Date().toISOString(),
        pathwayScores: results,
        bestMatch: this.findBestPathway(results),
        analysis: analysis,
        salesGuidance: this.generateSalesGuidance(results, analysis),
        confidence: this.calculateOverallConfidence(results, cvAnalysis)
      };

    } catch (error) {
      console.error('Error calculating enhanced pathway scores:', error);
      throw error;
    }
  }

  /**
   * Germany Student Visa Scoring Algorithm
   * Based on comprehensive assessment of academic and financial requirements
   */
  static async calculateGermanyStudentVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};
    const strengths = [];
    const concerns = [];

    try {
      // Education Assessment (40 points)
      const educationScore = this.evaluateEducationForGermanVisa(userProfile, cvAnalysis);
      breakdown.education = educationScore;
      score += educationScore.score;

      if (educationScore.score >= 30) {
        strengths.push("Strong educational background with recognized qualifications");
      } else if (educationScore.score < 20) {
        concerns.push("Educational qualifications may need further verification");
      }

      // Language Proficiency (25 points)
      const languageScore = this.evaluateGermanLanguageProficiency(userProfile, cvAnalysis);
      breakdown.language = languageScore;
      score += languageScore.score;

      if (languageScore.score >= 20) {
        strengths.push("Good German language proficiency for academic success");
      } else if (languageScore.score < 15) {
        concerns.push("Language skills may need improvement for university admission");
      }

      // Financial Capacity (20 points)
      const financialScore = this.evaluateFinancialCapacity(userProfile, cvAnalysis);
      breakdown.financial = financialScore;
      score += financialScore.score;

      if (financialScore.score >= 15) {
        strengths.push("Sufficient financial resources for living expenses");
      } else if (financialScore.score < 10) {
        concerns.push("Financial documentation may need strengthening");
      }

      // Age Factor (10 points)
      const ageScore = this.evaluateAgeFactor(userProfile);
      breakdown.age = ageScore;
      score += ageScore.score;

      // Academic Relevance (5 points)
      const relevanceScore = this.evaluateAcademicRelevance(userProfile, cvAnalysis);
      breakdown.relevance = relevanceScore;
      score += relevanceScore.score;

      // Calculate conversion probability
      const conversionProbability = this.calculateConversionProbability(score, maxScore, breakdown);

      return {
        pathwayName: "Germany Student Visa",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: Math.round((score / maxScore) * 100),
        breakdown: breakdown,
        isEligible: score >= 60,
        conversionProbability: conversionProbability,
        strengths: strengths,
        concerns: concerns,
        recommendedActions: this.generateStudentVisaRecommendations(breakdown),
        processingTime: "60-90 days",
        successRate: "75-85%",
        estimatedCost: "€10,000 - €15,000 per year"
      };

    } catch (error) {
      console.error('Error calculating Germany Student Visa score:', error);
      return this.getDefaultScoreResult("Germany Student Visa");
    }
  }

  /**
   * Chancenkarte (Opportunity Card) Scoring Algorithm
   * Points-based system for skilled workers seeking employment in Germany
   */
  static async calculateChancenkarteScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};
    const strengths = [];
    const concerns = [];

    try {
      // Qualification Recognition (20 points)
      const qualificationScore = this.evaluateQualificationRecognition(userProfile, cvAnalysis);
      breakdown.qualification = qualificationScore;
      score += qualificationScore.score;

      // Professional Experience (25 points)
      const experienceScore = this.evaluateProfessionalExperience(userProfile, cvAnalysis);
      breakdown.experience = experienceScore;
      score += experienceScore.score;

      if (experienceScore.score >= 20) {
        strengths.push("Substantial professional experience in high-demand field");
      } else if (experienceScore.score < 15) {
        concerns.push("Limited professional experience may reduce chances");
      }

      // Language Skills (15 points)
      const languageScore = this.evaluateLanguageSkillsForChancenkarte(userProfile, cvAnalysis);
      breakdown.language = languageScore;
      score += languageScore.score;

      // Age (10 points)
      const ageScore = this.evaluateAgeForChancenkarte(userProfile);
      breakdown.age = ageScore;
      score += ageScore.score;

      // German Connections (10 points)
      const connectionsScore = this.evaluateGermanConnections(userProfile);
      breakdown.connections = connectionsScore;
      score += connectionsScore.score;

      // Job Market Demand (15 points)
      const demandScore = this.evaluateJobMarketDemand(userProfile, cvAnalysis);
      breakdown.demand = demandScore;
      score += demandScore.score;

      // Adaptability Factors (5 points)
      const adaptabilityScore = this.evaluateAdaptability(userProfile);
      breakdown.adaptability = adaptabilityScore;
      score += adaptabilityScore.score;

      // Calculate conversion probability
      const conversionProbability = this.calculateConversionProbability(score, maxScore, breakdown);

      return {
        pathwayName: "Chancenkarte (Opportunity Card)",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: Math.round((score / maxScore) * 100),
        breakdown: breakdown,
        isEligible: score >= 65,
        conversionProbability: conversionProbability,
        strengths: strengths,
        concerns: concerns,
        recommendedActions: this.generateChancenkarteRecommendations(breakdown),
        processingTime: "4-6 weeks",
        successRate: "70-80%",
        estimatedCost: "€75 - €100 application fee"
      };

    } catch (error) {
      console.error('Error calculating Chancenkarte score:', error);
      return this.getDefaultScoreResult("Chancenkarte (Opportunity Card)");
    }
  }

  /**
   * Canada Student Visa Scoring Algorithm
   * Comprehensive assessment for Canadian study permit applications
   */
  static async calculateCanadaStudentVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};
    const strengths = [];
    const concerns = [];

    try {
      // Academic Standing (30 points)
      const academicScore = this.evaluateAcademicStanding(userProfile, cvAnalysis);
      breakdown.academic = academicScore;
      score += academicScore.score;

      if (academicScore.score >= 25) {
        strengths.push("Strong academic background meets Canadian standards");
      } else if (academicScore.score < 20) {
        concerns.push("Academic qualifications may need additional assessment");
      }

      // Financial Proof (25 points)
      const financialScore = this.evaluateFinancialProofForCanada(userProfile, cvAnalysis);
      breakdown.financial = financialScore;
      score += financialScore.score;

      if (financialScore.score >= 20) {
        strengths.push("Adequate financial documentation for first year expenses");
      } else if (financialScore.score < 15) {
        concerns.push("Financial proof may need strengthening for visa approval");
      }

      // Language Proficiency (20 points)
      const languageScore = this.evaluateEnglishOrFrenchProficiency(userProfile, cvAnalysis);
      breakdown.language = languageScore;
      score += languageScore.score;

      // Study Plan Clarity (15 points)
      const studyPlanScore = this.evaluateStudyPlanClarity(userProfile, cvAnalysis);
      breakdown.studyPlan = studyPlanScore;
      score += studyPlanScore.score;

      // Ties to Home Country (10 points)
      const tiesScore = this.evaluateTiesToHomeCountry(userProfile);
      breakdown.ties = tiesScore;
      score += tiesScore.score;

      // Calculate conversion probability
      const conversionProbability = this.calculateConversionProbability(score, maxScore, breakdown);

      return {
        pathwayName: "Canada Student Visa",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: Math.round((score / maxScore) * 100),
        breakdown: breakdown,
        isEligible: score >= 60,
        conversionProbability: conversionProbability,
        strengths: strengths,
        concerns: concerns,
        recommendedActions: this.generateCanadaStudentVisaRecommendations(breakdown),
        processingTime: "8-12 weeks",
        successRate: "80-85%",
        estimatedCost: "CAD $150 application fee + proof of funds"
      };

    } catch (error) {
      console.error('Error calculating Canada Student Visa score:', error);
      return this.getDefaultScoreResult("Canada Student Visa");
    }
  }

  /**
   * Job Seeker Visa Scoring Algorithm
   * For Germany job seeker visa
   */
  static async calculateJobSeekerVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};

    try {
      // Qualification Recognition (30 points)
      score += this.evaluateQualificationRecognition(userProfile, cvAnalysis).score * 0.3;

      // Professional Experience (30 points)
      score += this.evaluateProfessionalExperience(userProfile, cvAnalysis).score * 0.3;

      // Language Skills (20 points)
      score += this.evaluateGermanLanguageProficiency(userProfile, cvAnalysis).score * 0.2;

      // Financial Capacity (10 points)
      score += this.evaluateFinancialCapacity(userProfile, cvAnalysis).score * 0.1;

      // Age (10 points)
      score += this.evaluateAgeForChancenkarte(userProfile).score * 0.1;

      const percentageScore = Math.round(score);

      return {
        pathwayName: "Germany Job Seeker Visa",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        breakdown: breakdown,
        isEligible: score >= 60,
        conversionProbability: Math.min(percentageScore + 10, 95),
        processingTime: "4-6 weeks",
        successRate: "65-75%"
      };

    } catch (error) {
      console.error('Error calculating Job Seeker Visa score:', error);
      return this.getDefaultScoreResult("Germany Job Seeker Visa");
    }
  }

  /**
   * Work Permit Scoring Algorithm
   * For various work permit categories
   */
  static async calculateWorkPermitScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    try {
      // Job Offer (40 points)
      score += userProfile.jobOffer ? 40 : 0;

      // Qualification Recognition (25 points)
      score += this.evaluateQualificationRecognition(userProfile, cvAnalysis).score * 0.25;

      // Professional Experience (20 points)
      score += this.evaluateProfessionalExperience(userProfile, cvAnalysis).score * 0.2;

      // Language Skills (15 points)
      score += this.evaluateGermanLanguageProficiency(userProfile, cvAnalysis).score * 0.15;

      const percentageScore = Math.round(score);

      return {
        pathwayName: "Germany Work Permit",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        isEligible: score >= 50, // Lower threshold with job offer
        conversionProbability: userProfile.jobOffer ? Math.min(percentageScore + 20, 95) : percentageScore,
        processingTime: "8-12 weeks",
        successRate: "70-80%"
      };

    } catch (error) {
      console.error('Error calculating Work Permit score:', error);
      return this.getDefaultScoreResult("Germany Work Permit");
    }
  }

  /**
   * Express Entry Scoring Algorithm for Canada
   */
  static async calculateExpressEntryScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    try {
      // Age (30 points)
      score += this.calculateExpressEntryAgePoints(userProfile);

      // Education (25 points)
      score += this.calculateExpressEntryEducationPoints(userProfile, cvAnalysis);

      // Language Skills (28 points)
      score += this.calculateExpressEntryLanguagePoints(userProfile, cvAnalysis);

      // Work Experience (17 points)
      score += this.calculateExpressEntryExperiencePoints(userProfile, cvAnalysis);

      const percentageScore = Math.round(score);

      return {
        pathwayName: "Canada Express Entry",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        isEligible: score >= 67, // CRS cutoff
        conversionProbability: score >= 67 ? Math.min(percentageScore + 15, 90) : percentageScore,
        processingTime: "6 months",
        successRate: "Varies by CRS score"
      };

    } catch (error) {
      console.error('Error calculating Express Entry score:', error);
      return this.getDefaultScoreResult("Canada Express Entry");
    }
  }

  /**
   * Provincial Nominee Program Scoring
   */
  static async calculateProvincialNomineeScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    try {
      // Provincial Labor Market Demand (40 points)
      score += this.evaluateProvincialDemand(userProfile, cvAnalysis).score;

      // Settlement Funds (20 points)
      score += this.evaluateSettlementFunds(userProfile).score;

      // Intent to Reside (20 points)
      score += this.evaluateIntentToReside(userProfile).score;

      // Adaptability (20 points)
      score += this.evaluateAdaptability(userProfile).score;

      const percentageScore = Math.round(score);

      return {
        pathwayName: "Provincial Nominee Program",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        isEligible: score >= 60,
        conversionProbability: Math.min(percentageScore + 25, 95),
        processingTime: "6-19 months",
        successRate: "75-85%"
      };

    } catch (error) {
      console.error('Error calculating PNP score:', error);
      return this.getDefaultScoreResult("Provincial Nominee Program");
    }
  }

  // Helper methods for specific evaluations

  static evaluateEducationForGermanVisa(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 40;
    const details = {};

    const education = userProfile.education || {};
    const cvEducation = cvAnalysis?.education || {};

    // Highest education level (20 points)
    if (education.highestEducation === 'masters' || cvEducation.level === 'Master') {
      score += 20;
      details.level = "Master's degree - Full points";
    } else if (education.highestEducation === 'bachelors' || cvEducation.level === 'Bachelor') {
      score += 18;
      details.level = "Bachelor's degree - Near full points";
    } else if (education.highestEducation === 'diploma') {
      score += 15;
      details.level = "Diploma - Good points";
    } else {
      score += 10;
      details.level = "Other education - Partial points";
    }

    // Field relevance (10 points)
    const relevantFields = ['engineering', 'it', 'science', 'medicine', 'business'];
    const fieldOfStudy = (education.fieldOfStudy || cvEducation.field || '').toLowerCase();

    if (relevantFields.some(field => fieldOfStudy.includes(field))) {
      score += 10;
      details.field = "Field in high demand - Full points";
    } else {
      score += 5;
      details.field = "General field - Partial points";
    }

    // Academic performance (5 points)
    if (education.gpa >= 3.5 || education.percentage >= 80) {
      score += 5;
      details.performance = "Excellent academic performance";
    } else if (education.gpa >= 3.0 || education.percentage >= 70) {
      score += 3;
      details.performance = "Good academic performance";
    } else {
      score += 1;
      details.performance = "Standard academic performance";
    }

    // Institution recognition (5 points)
    if (education.university && education.university.toLowerCase().includes('recognized')) {
      score += 5;
      details.institution = "Recognized institution";
    } else {
      score += 2;
      details.institution = "Standard institution";
    }

    return {
      score: score,
      maxScore: maxScore,
      percentage: Math.round((score / maxScore) * 100),
      details: details
    };
  }

  static evaluateGermanLanguageProficiency(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 25;
    const details = {};

    const languageSkills = userProfile.languageSkills || {};
    const cvLanguage = cvAnalysis?.languageSkills || {};

    // German proficiency (15 points)
    const germanLevel = languageSkills.german || cvLanguage.german || 'none';

    switch (germanLevel.toLowerCase()) {
      case 'native':
      case 'c2':
        score += 15;
        details.german = "Native/Fluent German - Full points";
        break;
      case 'c1':
        score += 14;
        details.german = "Advanced German (C1)";
        break;
      case 'b2':
        score += 12;
        details.german = "Upper Intermediate German (B2)";
        break;
      case 'b1':
        score += 10;
        details.german = "Intermediate German (B1)";
        break;
      case 'a2':
        score += 7;
        details.german = "Elementary German (A2)";
        break;
      case 'a1':
        score += 5;
        details.german = "Beginner German (A1)";
        break;
      default:
        score += 0;
        details.german = "No German proficiency";
    }

    // English proficiency (10 points)
    const englishLevel = languageSkills.english || cvLanguage.english || 'none';

    switch (englishLevel.toLowerCase()) {
      case 'native':
      case 'c2':
        score += 10;
        details.english = "Native/Fluent English - Full points";
        break;
      case 'c1':
      case 'advanced':
        score += 9;
        details.english = "Advanced English";
        break;
      case 'b2':
      case 'intermediate':
        score += 8;
        details.english = "Intermediate English";
        break;
      case 'b1':
        score += 6;
        details.english = "Lower Intermediate English";
        break;
      default:
        score += 3;
        details.english = "Basic English";
    }

    return {
      score: score,
      maxScore: maxScore,
      percentage: Math.round((score / maxScore) * 100),
      details: details
    };
  }

  static evaluateFinancialCapacity(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 20;
    const details = {};

    const financialInfo = userProfile.financialInfo || {};

    // Blocked account amount (10 points)
    const blockedAccount = parseFloat(financialInfo.blockedAccount || 0);
    const requiredAmount = 11000; // €11,000 for 2024

    if (blockedAccount >= requiredAmount) {
      score += 10;
      details.blockedAccount = "Sufficient blocked account amount";
    } else {
      const percentage = (blockedAccount / requiredAmount) * 100;
      score += Math.round(10 * (percentage / 100));
      details.blockedAccount = `Blocked account covers ${percentage.toFixed(1)}% of required amount`;
    }

    // Monthly income/sponsorship (5 points)
    const monthlyIncome = parseFloat(financialInfo.monthlyIncome || 0);

    if (monthlyIncome >= 1000) {
      score += 5;
      details.income = "Strong monthly income/sponsorship";
    } else if (monthlyIncome >= 500) {
      score += 3;
      details.income = "Moderate monthly income/sponsorship";
    } else {
      score += 1;
      details.income = "Limited monthly income/sponsorship";
    }

    // Additional funds (5 points)
    const additionalFunds = parseFloat(financialInfo.additionalFunds || 0);

    if (additionalFunds >= 5000) {
      score += 5;
      details.additional = "Strong additional financial resources";
    } else if (additionalFunds >= 2000) {
      score += 3;
      details.additional = "Moderate additional financial resources";
    } else {
      score += 1;
      details.additional = "Limited additional financial resources";
    }

    return {
      score: score,
      maxScore: maxScore,
      percentage: Math.round((score / maxScore) * 100),
      details: details
    };
  }

  static evaluateAgeFactor(userProfile) {
    let score = 0;
    const maxScore = 10;
    const details = {};

    const dateOfBirth = userProfile.personalInfo?.dateOfBirth;

    if (dateOfBirth) {
      const age = this.calculateAge(dateOfBirth);

      if (age >= 18 && age <= 25) {
        score = 10;
        details.age = "Ideal age for student visa (18-25)";
      } else if (age >= 26 && age <= 30) {
        score = 8;
        details.age = "Good age for student visa (26-30)";
      } else if (age >= 31 && age <= 35) {
        score = 6;
        details.age = "Acceptable age for student visa (31-35)";
      } else if (age >= 17 && age <= 40) {
        score = 4;
        details.age = "Borderline age for student visa";
      } else {
        score = 2;
        details.age = "Age may be a concern for visa approval";
      }
    } else {
      score = 5;
      details.age = "Age not provided - neutral score";
    }

    return {
      score: score,
      maxScore: maxScore,
      percentage: Math.round((score / maxScore) * 100),
      details: details
    };
  }

  static evaluateAcademicRelevance(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 5;
    const details = {};

    const education = userProfile.education || {};
    const cvEducation = cvAnalysis?.education || {};

    // Relevance to German job market (3 points)
    const relevantFields = ['stem', 'engineering', 'it', 'healthcare', 'skilled trades'];
    const fieldOfStudy = (education.fieldOfStudy || cvEducation.field || '').toLowerCase();

    if (relevantFields.some(field => fieldOfStudy.includes(field))) {
      score += 3;
      details.relevance = "Field highly relevant to German job market";
    } else {
      score += 1;
      details.relevance = "Field has moderate relevance";
    }

    // German language in education (2 points)
    if (education.languageOfInstruction === 'German' || cvEducation.languageOfInstruction === 'German') {
      score += 2;
      details.language = "German language of instruction - advantage";
    } else {
      score += 0;
      details.language = "Non-German language of instruction";
    }

    return {
      score: score,
      maxScore: maxScore,
      percentage: Math.round((score / maxScore) * 100),
      details: details
    };
  }

  // Additional evaluation methods for other pathways...

  static evaluateQualificationRecognition(userProfile, cvAnalysis) {
    // Implementation for qualification recognition scoring
    let score = 15; // Base score

    if (cvAnalysis?.qualification?.recognized) {
      score += 5;
    }

    return { score: Math.min(score, 20), maxScore: 20, percentage: Math.round((Math.min(score, 20) / 20) * 100) };
  }

  static evaluateProfessionalExperience(userProfile, cvAnalysis) {
    let score = 0;
    const experience = userProfile.experience || [];
    const cvExperience = cvAnalysis?.experience || [];

    const totalYears = Math.max(
      experience.reduce((sum, exp) => sum + (exp.years || 0), 0),
      cvExperience.reduce((sum, exp) => sum + (exp.years || 0), 0)
    );

    if (totalYears >= 5) score = 25;
    else if (totalYears >= 3) score = 20;
    else if (totalYears >= 2) score = 15;
    else if (totalYears >= 1) score = 10;
    else score = 5;

    return { score, maxScore: 25, percentage: Math.round((score / 25) * 100) };
  }

  static evaluateLanguageSkillsForChancenkarte(userProfile, cvAnalysis) {
    // Similar to German language evaluation but with different criteria
    return this.evaluateGermanLanguageProficiency(userProfile, cvAnalysis);
  }

  static evaluateAgeForChancenkarte(userProfile) {
    return this.evaluateAgeFactor(userProfile);
  }

  static evaluateGermanConnections(userProfile) {
    let score = 0;
    // Check for previous stays, family connections, etc.
    if (userProfile.germanConnections?.previousStay) score += 5;
    if (userProfile.germanConnections?.family) score += 3;
    if (userProfile.germanConnections?.education) score += 2;

    return { score: Math.min(score, 10), maxScore: 10, percentage: Math.round((Math.min(score, 10) / 10) * 100) };
  }

  static evaluateJobMarketDemand(userProfile, cvAnalysis) {
    let score = 10; // Base score

    const inDemandFields = ['it', 'engineering', 'healthcare', 'skilled trades'];
    const field = (userProfile.experience?.[0]?.industry || cvAnalysis?.experience?.[0]?.field || '').toLowerCase();

    if (inDemandFields.some(demand => field.includes(demand))) {
      score += 5;
    }

    return { score: Math.min(score, 15), maxScore: 15, percentage: Math.round((Math.min(score, 15) / 15) * 100) };
  }

  static evaluateAdaptability(userProfile) {
    let score = 0;
    if (userProfile.languageSkills?.english || userProfile.languageSkills?.german) score += 2;
    if (userProfile.previousInternationalExperience) score += 2;
    if (userProfile.adaptabilityScore) score += 1;

    return { score: Math.min(score, 5), maxScore: 5, percentage: Math.round((Math.min(score, 5) / 5) * 100) };
  }

  // Additional helper methods for Canada pathways...

  static evaluateAcademicStanding(userProfile, cvAnalysis) {
    let score = 0;
    const education = userProfile.education || {};

    if (education.highestEducation === 'masters') score = 30;
    else if (education.highestEducation === 'bachelors') score = 25;
    else if (education.highestEducation === 'diploma') score = 20;
    else score = 15;

    return { score, maxScore: 30, percentage: Math.round((score / 30) * 100) };
  }

  static evaluateFinancialProofForCanada(userProfile, cvAnalysis) {
    let score = 0;
    const financial = userProfile.financialInfo || {};
    const requiredAnnual = 20000; // CAD 20,000 approximate

    const annualFunds = parseFloat(financial.annualFunds || 0);
    if (annualFunds >= requiredAnnual) score = 25;
    else score = Math.round(25 * (annualFunds / requiredAnnual));

    return { score: Math.min(score, 25), maxScore: 25, percentage: Math.round((Math.min(score, 25) / 25) * 100) };
  }

  static evaluateEnglishOrFrenchProficiency(userProfile, cvAnalysis) {
    let score = 0;
    const language = userProfile.languageSkills || {};

    if (language.ieltsScore >= 7.5 || language.celpipScore >= 7) score = 20;
    else if (language.ieltsScore >= 6.5 || language.celpipScore >= 6) score = 15;
    else if (language.ieltsScore >= 6.0 || language.celpipScore >= 5) score = 10;
    else score = 5;

    return { score, maxScore: 20, percentage: Math.round((score / 20) * 100) };
  }

  static evaluateStudyPlanClarity(userProfile, cvAnalysis) {
    let score = 10; // Base score

    if (userProfile.studyPlan?.clearGoals) score += 3;
    if (userProfile.studyPlan?.careerAlignment) score += 2;

    return { score, maxScore: 15, percentage: Math.round((score / 15) * 100) };
  }

  static evaluateTiesToHomeCountry(userProfile) {
    let score = 5; // Base score

    if (userProfile.tiesToHomeCountry?.family) score += 2;
    if (userProfile.tiesToHomeCountry?.property) score += 2;
    if (userProfile.tiesToHomeCountry?.employment) score += 1;

    return { score: Math.min(score, 10), maxScore: 10, percentage: Math.round((Math.min(score, 10) / 10) * 100) };
  }

  // Utility methods

  static calculateAge(dateOfBirth) {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  static calculateConversionProbability(score, maxScore, breakdown) {
    const basePercentage = (score / maxScore) * 100;
    let probability = basePercentage;

    // Adjust based on key factors
    if (breakdown.financial?.percentage >= 80) probability += 5;
    if (breakdown.education?.percentage >= 80) probability += 5;
    if (breakdown.language?.percentage >= 70) probability += 5;

    // Cap at reasonable limits
    return Math.min(Math.max(probability, 20), 95);
  }

  static findBestPathway(results) {
    let bestPathway = null;
    let highestScore = 0;

    for (const [pathwayKey, result] of Object.entries(results)) {
      if (result.percentageScore > highestScore) {
        highestScore = result.percentageScore;
        bestPathway = pathwayKey;
      }
    }

    return {
      pathway: bestPathway,
      score: highestScore,
      result: bestPathway ? results[bestPathway] : null
    };
  }

  static generateComprehensiveAnalysis(userProfile, results, cvAnalysis) {
    const analysis = {
      overallStrengths: [],
      areasForImprovement: [],
      riskFactors: [],
      competitiveAdvantages: [],
      marketAlignment: {},
      recommendedNextSteps: []
    };

    // Analyze all pathway results
    Object.values(results).forEach(result => {
      if (result.strengths) {
        analysis.overallStrengths.push(...result.strengths);
      }
      if (result.concerns) {
        analysis.areasForImprovement.push(...result.concerns);
      }
    });

    // Remove duplicates
    analysis.overallStrengths = [...new Set(analysis.overallStrengths)];
    analysis.areasForImprovement = [...new Set(analysis.areasForImprovement)];

    return analysis;
  }

  static generateSalesGuidance(results, analysis) {
    return {
      leadScore: this.calculateLeadScore(results),
      conversionLikelihood: this.calculateConversionLikelihood(results),
      recommendedApproach: this.getRecommendedApproach(results),
      keySellingPoints: this.getKeySellingPoints(results),
      potentialObjections: this.getPotentialObjections(results),
      followUpStrategy: this.getFollowUpStrategy(results),
      pricingStrategy: this.getPricingStrategy(results)
    };
  }

  static calculateLeadScore(results) {
    const bestScore = Math.max(...Object.values(results).map(r => r.percentageScore || 0));
    return Math.min(bestScore + 15, 100);
  }

  static calculateConversionLikelihood(results) {
    const eligiblePathways = Object.values(results).filter(r => r.isEligible).length;
    const totalPathways = Object.keys(results).length;

    return {
      percentage: Math.round((eligiblePathways / totalPathways) * 100),
      timeframe: this.getConversionTimeframe(results),
      confidence: this.calculateOverallConfidence(results)
    };
  }

  static calculateOverallConfidence(results, cvAnalysis = null) {
    let confidence = 0.7; // Base confidence

    // Increase confidence if multiple pathways are eligible
    const eligiblePathways = Object.values(results).filter(r => r.isEligible).length;
    confidence += (eligiblePathways * 0.05);

    // Increase confidence if CV analysis is available
    if (cvAnalysis) {
      confidence += 0.1;
    }

    return Math.min(confidence, 0.95);
  }

  // Recommendation generators
  static generateStudentVisaRecommendations(breakdown) {
    const recommendations = [];

    if (breakdown.language?.percentage < 70) {
      recommendations.push("Improve German language skills to at least B1 level");
    }

    if (breakdown.financial?.percentage < 80) {
      recommendations.push("Secure additional funding or sponsorship");
    }

    if (breakdown.education?.percentage < 75) {
      recommendations.push("Obtain additional academic documentation");
    }

    return recommendations;
  }

  static generateChancenkarteRecommendations(breakdown) {
    const recommendations = [];

    if (breakdown.qualification?.percentage < 70) {
      recommendations.push("Initiate qualification recognition process");
    }

    if (breakdown.experience?.percentage < 70) {
      recommendations.push("Gain more relevant work experience");
    }

    if (breakdown.language?.percentage < 60) {
      recommendations.push("Improve language skills (German/English)");
    }

    return recommendations;
  }

  static generateCanadaStudentVisaRecommendations(breakdown) {
    const recommendations = [];

    if (breakdown.financial?.percentage < 80) {
      recommendations.push("Strengthen financial documentation");
    }

    if (breakdown.academic?.percentage < 75) {
      recommendations.push("Obtain credential assessment if required");
    }

    if (breakdown.language?.percentage < 70) {
      recommendations.push("Improve English/French language test scores");
    }

    return recommendations;
  }

  // Additional helper methods
  static getConversionTimeframe(results) {
    const bestPathway = this.findBestPathway(results);
    if (bestPathway.result?.processingTime) {
      return bestPathway.result.processingTime;
    }
    return "3-6 months";
  }

  static getRecommendedApproach(results) {
    const bestScore = Math.max(...Object.values(results).map(r => r.percentageScore || 0));

    if (bestScore >= 80) {
      return "Direct consultation with premium package recommendation";
    } else if (bestScore >= 60) {
      return "Standard consultation with pathway optimization";
    } else {
      return "Profile improvement consultation before application";
    }
  }

  static getKeySellingPoints(results) {
    const points = [];
    const bestPathway = this.findBestPathway(results);

    if (bestPathway.result) {
      points.push(`${bestPathway.result.pathwayName} with ${bestPathway.score}% match`);
      points.push(`Processing time: ${bestPathway.result.processingTime || 'Standard'}`);
      points.push(`Success rate: ${bestPathway.result.successRate || 'Good'}`);
    }

    return points;
  }

  static getPotentialObjections(results) {
    const objections = [];

    Object.values(results).forEach(result => {
      if (result.concerns) {
        objections.push(...result.concerns);
      }
    });

    return [...new Set(objections)];
  }

  static getFollowUpStrategy(results) {
    const bestScore = Math.max(...Object.values(results).map(r => r.percentageScore || 0));

    if (bestScore >= 80) {
      return "Immediate follow-up with detailed pathway information";
    } else if (bestScore >= 60) {
      return "Schedule consultation within 3-5 business days";
    } else {
      return "Send profile improvement resources, follow up in 2 weeks";
    }
  }

  static getPricingStrategy(results) {
    const bestScore = Math.max(...Object.values(results).map(r => r.percentageScore || 0));

    if (bestScore >= 80) {
      return "Premium package with priority processing";
    } else if (bestScore >= 60) {
      return "Standard package with comprehensive support";
    } else {
      return "Basic package with upgrade options";
    }
  }

  // Additional Canada-specific methods
  static calculateExpressEntryAgePoints(userProfile) {
    const age = this.calculateAge(userProfile.personalInfo?.dateOfBirth);

    if (age >= 20 && age <= 29) return 30;
    if (age >= 18 && age <= 19) return 28;
    if (age >= 30 && age <= 34) return 25;
    if (age >= 35 && age <= 39) return 20;
    if (age >= 40 && age <= 44) return 15;
    if (age >= 45) return 0;

    return 15; // Default
  }

  static calculateExpressEntryEducationPoints(userProfile, cvAnalysis) {
    const education = userProfile.education || {};

    if (education.highestEducation === 'masters' || education.highestEducation === 'phd') return 25;
    if (education.highestEducation === 'bachelors') return 20;
    if (education.highestEducation === 'diploma') return 15;

    return 10;
  }

  static calculateExpressEntryLanguagePoints(userProfile, cvAnalysis) {
    const language = userProfile.languageSkills || {};
    let points = 0;

    // First official language
    if (language.ieltsScore >= 8) points += 24;
    else if (language.ieltsScore >= 7) points += 20;
    else if (language.ieltsScore >= 6) points += 16;

    // Second official language
    if (language.frenchLevel) points += 4;

    return Math.min(points, 28);
  }

  static calculateExpressEntryExperiencePoints(userProfile, cvAnalysis) {
    const experience = userProfile.experience || [];
    const totalYears = experience.reduce((sum, exp) => sum + (exp.years || 0), 0);

    if (totalYears >= 5) return 17;
    if (totalYears >= 4) return 14;
    if (totalYears >= 3) return 11;
    if (totalYears >= 2) return 9;
    if (totalYears >= 1) return 7;

    return 0;
  }

  static evaluateProvincialDemand(userProfile, cvAnalysis) {
    // Simplified provincial demand evaluation
    return { score: 20, maxScore: 40, percentage: 50 };
  }

  static evaluateSettlementFunds(userProfile) {
    const financial = userProfile.financialInfo || {};
    const funds = parseFloat(financial.settlementFunds || 0);
    const required = 13000; // Approximate CAD requirement

    const score = Math.min(20, Math.round(20 * (funds / required)));
    return { score, maxScore: 20, percentage: Math.round((score / 20) * 100) };
  }

  static evaluateIntentToReside(userProfile) {
    let score = 10; // Base score

    if (userProfile.provincialTie) score += 5;
    if (userProfile.jobSearchInProvince) score += 5;

    return { score: Math.min(score, 20), maxScore: 20, percentage: Math.round((Math.min(score, 20) / 20) * 100) };
  }

  static getDefaultScoreResult(pathwayName) {
    return {
      pathwayName: pathwayName,
      totalScore: 0,
      maxScore: 100,
      percentageScore: 0,
      breakdown: {},
      isEligible: false,
      conversionProbability: 0,
      strengths: [],
      concerns: ["Unable to calculate score due to missing data"],
      recommendedActions: ["Please complete your profile"],
      processingTime: "Varies",
      successRate: "Unknown"
    };
  }

  // ============ AUSTRALIA SCORING ALGORITHMS ============

  /**
   * Australia Student Visa Scoring Algorithm
   */
  static async calculateAustraliaStudentVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};
    const strengths = [];
    const concerns = [];

    try {
      // Academic Standing (35 points)
      const academicScore = this.evaluateAcademicStandingForAustralia(userProfile, cvAnalysis);
      breakdown.academic = academicScore;
      score += academicScore.score;

      if (academicScore.score >= 28) {
        strengths.push("Strong academic background meets Australian standards");
      }

      // English Proficiency (25 points)
      const languageScore = this.evaluateEnglishProficiencyForAustralia(userProfile, cvAnalysis);
      breakdown.language = languageScore;
      score += languageScore.score;

      // Financial Capacity (20 points)
      const financialScore = this.evaluateFinancialCapacityForAustralia(userProfile, cvAnalysis);
      breakdown.financial = financialScore;
      score += financialScore.score;

      if (financialScore.score >= 16) {
        strengths.push("Sufficient financial resources for Australian studies");
      }

      // Genuine Temporary Entrant (10 points)
      const gteScore = this.evaluateGTECriteria(userProfile, cvAnalysis);
      breakdown.gte = gteScore;
      score += gteScore.score;

      // Health Insurance (5 points)
      const healthScore = this.evaluateHealthInsuranceRequirements(userProfile);
      breakdown.health = healthScore;
      score += healthScore.score;

      // Age Factor (5 points)
      const ageScore = this.evaluateAgeForAustralia(userProfile);
      breakdown.age = ageScore;
      score += ageScore.score;

      const percentageScore = Math.round((score / maxScore) * 100);

      return {
        pathwayName: "Australia Student Visa",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        breakdown: breakdown,
        isEligible: score >= 60,
        conversionProbability: Math.min(percentageScore + 10, 90),
        strengths: strengths,
        concerns: concerns,
        recommendedActions: this.generateAustraliaStudentVisaRecommendations(breakdown),
        processingTime: "4-6 weeks",
        successRate: "85-90%",
        estimatedCost: "AUD $650 visa fee + AUD $21,041/year living costs"
      };

    } catch (error) {
      console.error('Error calculating Australia Student Visa score:', error);
      return this.getDefaultScoreResult("Australia Student Visa");
    }
  }

  /**
   * Australia Masters Programs Scoring
   */
  static async calculateAustraliaMastersScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    // Masters programs have higher academic requirements
    score += this.evaluateMastersLevelEducation(userProfile, cvAnalysis).score * 0.4;
    score += this.evaluateEnglishProficiencyForAustralia(userProfile, cvAnalysis).score * 0.25;
    score += this.evaluateFinancialCapacityForAustralia(userProfile, cvAnalysis).score * 0.2;
    score += this.evaluateGTECriteria(userProfile, cvAnalysis).score * 0.1;
    score += this.evaluateAgeForAustralia(userProfile).score * 0.05;

    const percentageScore = Math.round(score);

    return {
      pathwayName: "Australia Masters Program",
      totalScore: score,
      maxScore: maxScore,
      percentageScore: percentageScore,
      isEligible: score >= 65,
      conversionProbability: Math.min(percentageScore + 10, 92),
      processingTime: "4-8 weeks",
      successRate: "80-85%",
      estimatedCost: "AUD $25,000-50,000/year tuition"
    };
  }

  /**
   * Australia Graduate Visa Scoring
   */
  static async calculateAustraliaGraduateVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    // Requires completed Australian qualification
    score += this.evaluateAustralianQualification(userProfile, cvAnalysis).score * 0.4;
    score += this.evaluateEnglishProficiencyForAustralia(userProfile, cvAnalysis).score * 0.3;
    score += this.evaluateAustralianStudyRequirements(userProfile).score * 0.2;
    score += this.evaluateAgeForGraduateVisa(userProfile).score * 0.1;

    const percentageScore = Math.round(score);

    return {
      pathwayName: "Australia Graduate Visa (subclass 485)",
      totalScore: score,
      maxScore: maxScore,
      percentageScore: percentageScore,
      isEligible: score >= 60,
      conversionProbability: Math.min(percentageScore + 15, 95),
      processingTime: "75-85 days",
      successRate: "90-95%",
      estimatedCost: "AUD $1,735 application fee"
    };
  }

  // ============ UK SCORING ALGORITHMS ============

  /**
   * UK Student Visa Scoring Algorithm
   */
  static async calculateUKStudentVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};
    const strengths = [];
    const concerns = [];

    try {
      // Academic Requirements (40 points)
      const academicScore = this.evaluateAcademicStandingForUK(userProfile, cvAnalysis);
      breakdown.academic = academicScore;
      score += academicScore.score;

      if (academicScore.score >= 32) {
        strengths.push("Qualifications meet UK educational standards");
      }

      // English Language (25 points)
      const languageScore = this.evaluateEnglishProficiencyForUK(userProfile, cvAnalysis);
      breakdown.language = languageScore;
      score += languageScore.score;

      // Financial Requirements (20 points)
      const financialScore = this.evaluateFinancialCapacityForUK(userProfile, cvAnalysis);
      breakdown.financial = financialScore;
      score += financialScore.score;

      if (financialScore.score >= 16) {
        strengths.push("Adequate funds for UK studies");
      }

      // Confirmation of Acceptance for Studies (10 points)
      const casScore = this.evaluateCASRequirements(userProfile);
      breakdown.cas = casScore;
      score += casScore.score;

      // Ties to Home Country (5 points)
      const tiesScore = this.evaluateTiesToHomeCountry(userProfile);
      breakdown.ties = tiesScore;
      score += tiesScore.score;

      const percentageScore = Math.round((score / maxScore) * 100);

      return {
        pathwayName: "UK Student Visa",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        breakdown: breakdown,
        isEligible: score >= 60,
        conversionProbability: Math.min(percentageScore + 8, 88),
        strengths: strengths,
        concerns: concerns,
        recommendedActions: this.generateUKStudentVisaRecommendations(breakdown),
        processingTime: "3 weeks",
        successRate: "90-95%",
        estimatedCost: "£363 visa fee + £12,000-15,000/year living costs"
      };

    } catch (error) {
      console.error('Error calculating UK Student Visa score:', error);
      return this.getDefaultScoreResult("UK Student Visa");
    }
  }

  /**
   * UK Graduate Route Scoring
   */
  static async calculateUKGraduateRouteScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    score += this.evaluateUKQualification(userProfile, cvAnalysis).score * 0.5;
    score += this.evaluateEnglishProficiencyForUK(userProfile, cvAnalysis).score * 0.25;
    score += this.evaluateUKStudyRequirements(userProfile).score * 0.2;
    score += this.evaluateAgeForUK(userProfile).score * 0.05;

    const percentageScore = Math.round(score);

    return {
      pathwayName: "UK Graduate Route (Post-Study Work Visa)",
      totalScore: score,
      maxScore: maxScore,
      percentageScore: percentageScore,
      isEligible: score >= 65,
      conversionProbability: Math.min(percentageScore + 10, 95),
      processingTime: "8 weeks",
      successRate: "95-98%",
      estimatedCost: "£715 application fee"
    };
  }

  /**
   * UK Skilled Worker Visa Scoring
   */
  static async calculateUKSkilledWorkerScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    score += this.evaluateJobOffer(userProfile).score * 0.4;
    score += this.evaluateEnglishProficiencyForUK(userProfile, cvAnalysis).score * 0.25;
    score += this.evaluateQualificationRecognitionForUK(userProfile, cvAnalysis).score * 0.2;
    score += this.evaluateFinancialCapacityForUK(userProfile, cvAnalysis).score * 0.1;
    score += this.evaluateAgeForUK(userProfile).score * 0.05;

    const percentageScore = Math.round(score);

    return {
      pathwayName: "UK Skilled Worker Visa",
      totalScore: score,
      maxScore: maxScore,
      percentageScore: percentageScore,
      isEligible: score >= 60,
      conversionProbability: Math.min(percentageScore + 12, 92),
      processingTime: "3 weeks (priority) or 8 weeks (standard)",
      successRate: "85-90%",
      estimatedCost: "£610-1,408 application fee"
    };
  }

  // ============ NEW ZEALAND SCORING ALGORITHMS ============

  /**
   * New Zealand Student Visa Scoring Algorithm
   */
  static async calculateNewZealandStudentVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;
    const breakdown = {};
    const strengths = [];
    const concerns = [];

    try {
      // Academic Standing (35 points)
      const academicScore = this.evaluateAcademicStandingForNZ(userProfile, cvAnalysis);
      breakdown.academic = academicScore;
      score += academicScore.score;

      if (academicScore.score >= 28) {
        strengths.push("Strong academic background for NZ studies");
      }

      // English Proficiency (25 points)
      const languageScore = this.evaluateEnglishProficiencyForNZ(userProfile, cvAnalysis);
      breakdown.language = languageScore;
      score += languageScore.score;

      // Financial Capacity (20 points)
      const financialScore = this.evaluateFinancialCapacityForNZ(userProfile, cvAnalysis);
      breakdown.financial = financialScore;
      score += financialScore.score;

      if (financialScore.score >= 16) {
        strengths.push("Sufficient funds for NZ education");
      }

      // Health and Character (10 points)
      const healthScore = this.evaluateHealthAndCharacter(userProfile);
      breakdown.health = healthScore;
      score += healthScore.score;

      // Genuine Student Intent (10 points)
      const genuineScore = this.evaluateGenuineStudentIntent(userProfile, cvAnalysis);
      breakdown.genuine = genuineScore;
      score += genuineScore.score;

      const percentageScore = Math.round((score / maxScore) * 100);

      return {
        pathwayName: "New Zealand Student Visa",
        totalScore: score,
        maxScore: maxScore,
        percentageScore: percentageScore,
        breakdown: breakdown,
        isEligible: score >= 60,
        conversionProbability: Math.min(percentageScore + 10, 90),
        strengths: strengths,
        concerns: concerns,
        recommendedActions: this.generateNZStudentVisaRecommendations(breakdown),
        processingTime: "4-6 weeks",
        successRate: "85-90%",
        estimatedCost: "Contact for current visa fees and living costs"
      };

    } catch (error) {
      console.error('Error calculating NZ Student Visa score:', error);
      return this.getDefaultScoreResult("New Zealand Student Visa");
    }
  }

  /**
   * New Zealand Work Visa Scoring
   */
  static async calculateNewZealandWorkVisaScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    score += this.evaluateJobOffer(userProfile, 'NZ').score * 0.4;
    score += this.evaluateEnglishProficiencyForNZ(userProfile, cvAnalysis).score * 0.25;
    score += this.evaluateQualificationRecognitionForNZ(userProfile, cvAnalysis).score * 0.2;
    score += this.evaluateAgeForNZ(userProfile).score * 0.1;
    score += this.evaluateWorkExperienceForNZ(userProfile, cvAnalysis).score * 0.05;

    const percentageScore = Math.round(score);

    return {
      pathwayName: "New Zealand Work Visa",
      totalScore: score,
      maxScore: maxScore,
      percentageScore: percentageScore,
      isEligible: score >= 60,
      conversionProbability: Math.min(percentageScore + 12, 92),
      processingTime: "3-5 weeks",
      successRate: "80-85%",
      estimatedCost: "Contact for current visa fees"
    };
  }

  /**
   * New Zealand Skilled Migrant Category Scoring
   */
  static async calculateNewZealandSkilledMigrantScore(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 100;

    // Points-based system similar to Express Entry
    score += this.calculateNZAgePoints(userProfile).score * 0.25;
    score += this.calculateNZEducationPoints(userProfile, cvAnalysis).score * 0.25;
    score += this.calculateNZLanguagePoints(userProfile, cvAnalysis).score * 0.2;
    score += this.calculateNZExperiencePoints(userProfile, cvAnalysis).score * 0.2;
    score += this.calculateNZEmploymentPoints(userProfile).score * 0.1;

    const percentageScore = Math.round(score);

    return {
      pathwayName: "New Zealand Skilled Migrant Category",
      totalScore: score,
      maxScore: maxScore,
      percentageScore: percentageScore,
      isEligible: score >= 65,
      conversionProbability: Math.min(percentageScore + 15, 90),
      processingTime: "10-12 months",
      successRate: "75-85%",
      estimatedCost: "NZD $5,310-6,310 application fee"
    };
  }

  // ============ HELPER METHODS FOR AUSTRALIA ============

  static evaluateAcademicStandingForAustralia(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 35;
    const education = userProfile.education || {};

    if (education.highestEducation === 'masters' || education.highestEducation === 'phd') {
      score = 35;
    } else if (education.highestEducation === 'bachelors') {
      score = 30;
    } else if (education.highestEducation === 'diploma') {
      score = 25;
    } else {
      score = 20;
    }

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateEnglishProficiencyForAustralia(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 25;
    const language = userProfile.languageSkills || {};

    if (language.ieltsScore >= 7.5) score = 25;
    else if (language.ieltsScore >= 7.0) score = 22;
    else if (language.ieltsScore >= 6.5) score = 18;
    else if (language.ieltsScore >= 6.0) score = 15;
    else score = 10;

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateFinancialCapacityForAustralia(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 20;
    const financial = userProfile.financialInfo || {};

    const annualFunds = parseFloat(financial.annualFunds || 0);
    const required = 21041; // AUD living costs

    if (annualFunds >= required) score = 20;
    else score = Math.round(20 * (annualFunds / required));

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateGTECriteria(userProfile, cvAnalysis) {
    let score = 8; // Base score
    const studyPlan = userProfile.studyPlan || {};

    if (studyPlan.clearGoals) score += 2;

    return { score: Math.min(score, 10), maxScore: 10, percentage: Math.round((Math.min(score, 10) / 10) * 100) };
  }

  static evaluateHealthInsuranceRequirements(userProfile) {
    return { score: 5, maxScore: 5, percentage: 100 };
  }

  static evaluateAgeForAustralia(userProfile) {
    const age = this.calculateAge(userProfile.personalInfo?.dateOfBirth);

    if (age >= 18 && age <= 25) return { score: 5, maxScore: 5, percentage: 100 };
    if (age >= 26 && age <= 35) return { score: 4, maxScore: 5, percentage: 80 };
    if (age >= 36 && age <= 40) return { score: 3, maxScore: 5, percentage: 60 };

    return { score: 2, maxScore: 5, percentage: 40 };
  }

  static evaluateMastersLevelEducation(userProfile, cvAnalysis) {
    let score = 0;
    const education = userProfile.education || {};

    if (education.highestEducation === 'masters') score = 40;
    else if (education.highestEducation === 'bachelors') score = 35;
    else score = 25;

    return { score, maxScore: 40, percentage: Math.round((score / 40) * 100) };
  }

  static evaluateGraduateVisaRequirements(userProfile) {
    return { score: 10, maxScore: 10, percentage: 100 };
  }

  static evaluateAustralianQualification(userProfile, cvAnalysis) {
    return { score: 40, maxScore: 40, percentage: 100 };
  }

  static evaluateAustralianStudyRequirements(userProfile) {
    return { score: 20, maxScore: 20, percentage: 100 };
  }

  static evaluateAgeForGraduateVisa(userProfile) {
    return this.evaluateAgeForAustralia(userProfile);
  }

  static generateAustraliaStudentVisaRecommendations(breakdown) {
    const recommendations = [];

    if (breakdown.language?.percentage < 70) {
      recommendations.push("Improve IELTS score to at least 6.5 for better university options");
    }

    if (breakdown.financial?.percentage < 80) {
      recommendations.push("Secure additional funds or sponsorship for living costs");
    }

    return recommendations;
  }

  // ============ HELPER METHODS FOR UK ============

  static evaluateAcademicStandingForUK(userProfile, cvAnalysis) {
    let score = 0;
    const education = userProfile.education || {};

    if (education.highestEducation === 'masters' || education.highestEducation === 'phd') {
      score = 40;
    } else if (education.highestEducation === 'bachelors') {
      score = 35;
    } else if (education.highestEducation === 'diploma') {
      score = 28;
    } else {
      score = 25;
    }

    return { score, maxScore: 40, percentage: Math.round((score / 40) * 100) };
  }

  static evaluateEnglishProficiencyForUK(userProfile, cvAnalysis) {
    let score = 0;
    const language = userProfile.languageSkills || {};

    if (language.ieltsScore >= 7.5) score = 25;
    else if (language.ieltsScore >= 7.0) score = 22;
    else if (language.ieltsScore >= 6.5) score = 18;
    else if (language.ieltsScore >= 6.0) score = 15;
    else score = 10;

    return { score, maxScore: 25, percentage: Math.round((score / 25) * 100) };
  }

  static evaluateFinancialCapacityForUK(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 20;
    const financial = userProfile.financialInfo || {};

    // UK requires £12,000-15,000 for living costs outside London
    const annualFunds = parseFloat(financial.annualFunds || 0);
    const required = 12000; // Outside London

    if (annualFunds >= required) score = 20;
    else score = Math.round(20 * (annualFunds / required));

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateCASRequirements(userProfile) {
    return { score: 10, maxScore: 10, percentage: 100 };
  }

  static evaluateJobOffer(userProfile, country = 'UK') {
    let score = 0;
    const maxScore = country === 'UK' ? 40 : 40;

    if (userProfile.jobOffer) {
      score = 40;
    } else {
      score = 0;
    }

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateQualificationRecognitionForUK(userProfile, cvAnalysis) {
    return { score: 20, maxScore: 20, percentage: 100 };
  }

  static evaluateUKQualification(userProfile, cvAnalysis) {
    return { score: 50, maxScore: 50, percentage: 100 };
  }

  static evaluateUKStudyRequirements(userProfile) {
    return { score: 20, maxScore: 20, percentage: 100 };
  }

  static evaluateAgeForUK(userProfile) {
    const age = this.calculateAge(userProfile.personalInfo?.dateOfBirth);

    if (age >= 18 && age <= 30) return { score: 5, maxScore: 5, percentage: 100 };
    if (age >= 31 && age <= 35) return { score: 4, maxScore: 5, percentage: 80 };

    return { score: 3, maxScore: 5, percentage: 60 };
  }

  static generateUKStudentVisaRecommendations(breakdown) {
    const recommendations = [];

    if (breakdown.language?.percentage < 70) {
      recommendations.push("Improve IELTS score for university admission");
    }

    if (breakdown.financial?.percentage < 80) {
      recommendations.push("Ensure adequate funds for living expenses");
    }

    return recommendations;
  }

  // ============ HELPER METHODS FOR NEW ZEALAND ============

  static evaluateAcademicStandingForNZ(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 35;
    const education = userProfile.education || {};

    if (education.highestEducation === 'masters' || education.highestEducation === 'phd') {
      score = 35;
    } else if (education.highestEducation === 'bachelors') {
      score = 30;
    } else if (education.highestEducation === 'diploma') {
      score = 25;
    } else {
      score = 20;
    }

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateEnglishProficiencyForNZ(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 25;
    const language = userProfile.languageSkills || {};

    if (language.ieltsScore >= 7.0) score = 25;
    else if (language.ieltsScore >= 6.5) score = 22;
    else if (language.ieltsScore >= 6.0) score = 18;
    else score = 15;

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateFinancialCapacityForNZ(userProfile, cvAnalysis) {
    let score = 0;
    const maxScore = 20;

    // NZ requirements vary by program
    const financial = userProfile.financialInfo || {};
    const funds = parseFloat(financial.totalFunds || financial.fundsAvailable || 0);

    // Assume required amount of LKR 5M minimum
    const required = 5000000; // LKR 5M

    if (funds >= required) score = 20;
    else score = Math.round(20 * (funds / required));

    return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
  }

  static evaluateHealthAndCharacter(userProfile) {
    return { score: 10, maxScore: 10, percentage: 100 };
  }

  static evaluateGenuineStudentIntent(userProfile, cvAnalysis) {
    let score = 8;
    const studyPlan = userProfile.studyPlan || {};

    if (studyPlan.clearGoals) score += 2;

    return { score: Math.min(score, 10), maxScore: 10, percentage: Math.round((Math.min(score, 10) / 10) * 100) };
  }

  static evaluateWorkExperienceForNZ(userProfile, cvAnalysis) {
    const experience = userProfile.experience || [];
    const totalYears = experience.reduce((sum, exp) => sum + (exp.years || 0), 0);

    const score = Math.min(totalYears * 1, 5);

    return { score, maxScore: 5, percentage: Math.round((score / 5) * 100) };
  }

  static evaluateQualificationRecognitionForNZ(userProfile, cvAnalysis) {
    return { score: 20, maxScore: 20, percentage: 100 };
  }

  static evaluateAgeForNZ(userProfile) {
    const age = this.calculateAge(userProfile.personalInfo?.dateOfBirth);

    if (age >= 18 && age <= 30) return { score: 10, maxScore: 10, percentage: 100 };
    if (age >= 31 && age <= 40) return { score: 8, maxScore: 10, percentage: 80 };

    return { score: 5, maxScore: 10, percentage: 50 };
  }

  static calculateNZAgePoints(userProfile) {
    const age = this.calculateAge(userProfile.personalInfo?.dateOfBirth);

    if (age >= 20 && age <= 29) return { score: 25, maxScore: 25 };
    else if (age >= 30 && age <= 39) return { score: 20, maxScore: 25 };
    else if (age >= 40 && age <= 44) return { score: 15, maxScore: 25 };
    else return { score: 10, maxScore: 25 };
  }

  static calculateNZEducationPoints(userProfile, cvAnalysis) {
    const education = userProfile.education || {};

    if (education.highestEducation === 'phd') return { score: 25, maxScore: 25 };
    if (education.highestEducation === 'masters') return { score: 23, maxScore: 25 };
    if (education.highestEducation === 'bachelors') return { score: 20, maxScore: 25 };

    return { score: 15, maxScore: 25 };
  }

  static calculateNZLanguagePoints(userProfile, cvAnalysis) {
    const language = userProfile.languageSkills || {};
    let score = 0;

    if (language.ieltsScore >= 7.5) score = 20;
    else if (language.ieltsScore >= 7.0) score = 18;
    else if (language.ieltsScore >= 6.5) score = 15;

    return { score, maxScore: 20 };
  }

  static calculateNZExperiencePoints(userProfile, cvAnalysis) {
    const experience = userProfile.experience || [];
    const totalYears = experience.reduce((sum, exp) => sum + (exp.years || 0), 0);

    let score = Math.min(totalYears * 2, 20);

    return { score, maxScore: 20 };
  }

  static calculateNZEmploymentPoints(userProfile) {
    const score = userProfile.jobOffer ? 10 : 0;
    return { score, maxScore: 10 };
  }

  static generateNZStudentVisaRecommendations(breakdown) {
    const recommendations = [];

    if (breakdown.language?.percentage < 70) {
      recommendations.push("Improve English proficiency for better course options");
    }

    if (breakdown.financial?.percentage < 80) {
      recommendations.push("Increase proof of funds to meet NZ requirements");
    }

    return recommendations;
  }

  // ============ ADDITIONAL CANADA PATHWAYS ============

  static async calculateCanadaOneYearPGScore(userProfile, cvAnalysis) {
    const baseScore = await this.calculateCanadaStudentVisaScore(userProfile, cvAnalysis);
    return {
      ...baseScore,
      pathwayName: "Canada 1-Year PG Certificate",
      percentageScore: Math.round(baseScore.percentageScore * 1.05), // Slight boost for shorter program
      processingTime: "6-10 weeks",
      successRate: "80-85%"
    };
  }

  static async calculateCanadaTwoYearPGScore(userProfile, cvAnalysis) {
    const baseScore = await this.calculateCanadaStudentVisaScore(userProfile, cvAnalysis);
    return {
      ...baseScore,
      pathwayName: "Canada 2-Year PG Diploma",
      percentageScore: baseScore.percentageScore, // Standard score
      processingTime: "8-12 weeks",
      successRate: "80-85%",
      conversionProbability: Math.min(baseScore.conversionProbability + 5, 95) // Eligible for 3-year PGWP
    };
  }
}

export default EnhancedScoringEngine;